import type { UserDto } from './user.dto';
import type { AssignedSurveyDto } from './surveys/lib/assignedSurvey.dto';
import { z } from 'zod';

/** ADT fields a Guide can tag as inaccurate. */
export const HOSPITALIZATION_INACCURACY_FIELDS = [
  'hospital',
  'admitDate',
  'admitType',
  'admitComplaint',
  'dischargeDate',
  'dischargeDisposition',
  'icd10Code',
  'wrongMember',
  'other',
] as const;

/** ADT field that a Guide can tag as inaccurate. */
export type HospitalizationInaccuracyField =
  (typeof HOSPITALIZATION_INACCURACY_FIELDS)[number];

/** Append-only report describing potentially inaccurate hospitalization data. */
export interface HospitalizationInaccuracyReportDto {
  id: number;
  hospitalizationId: number;
  /** Staff user who filed it; the portal resolves the name from Redux. */
  reportedByUserId: string;
  taggedFields: HospitalizationInaccuracyField[];
  note: string | null;
  createdAt: number;
}

/** Validates a request to report potentially inaccurate hospitalization data. */
export const CreateHospitalizationInaccuracyReportSchema = z
  .object({
    taggedFields: z
      .array(z.enum(HOSPITALIZATION_INACCURACY_FIELDS))
      .max(HOSPITALIZATION_INACCURACY_FIELDS.length)
      .optional(),
    note: z.string().optional(),
  })
  .strict();

/** Request body for filing a hospitalization inaccuracy report. */
export type CreateHospitalizationInaccuracyReportRequest = z.infer<
  typeof CreateHospitalizationInaccuracyReportSchema
>;

export interface HospitalizationDto {
  id: number;
  noteId?: number;
  note: string;

  memberId: string;
  hospital: string;
  hospitalInfo: string;
  // Admit
  admitComplaint: string;
  admitNotificationTime: string;
  admitType: string;
  admitDate: string;
  icd10Code: string;
  // Discharge
  dischargeNotificationTime: string;
  dischargeDate: string;
  dischargeDisposition: string;
  // Follow Up
  followUpDate: string;
  followUpType: string;
  requiresFollowUp: boolean;
  expectedSurveyCompletion: string;
  assignedSurveyId: number;

  // Tracking Timestamps
  attemptedAt: number;
  offeredAt: number;

  interactionId?: number | null;
  avonNoteId?: string | null;

  member: UserDto;
  isBehavioralHealth: boolean;
  isEnrolledInBehavioralHealth: boolean;
  isAssumed: boolean;
  /** Number of Guide-filed inaccuracy reports; 0 when none. */
  inaccuracyReportCount: number;
}

export interface HospitalizationFollowUpCountsDto {
  lastUpdatedAt: number;
  notAttempted: number;
  notOffered: number;
}

export interface CurrentAndPendingHospitalizationsDto {
  meta: {
    total: number;
    offeredCount: number;
    attemptedCount: number;
  };
  data: Record<string, number[]>;
  hospitalizations: HospitalizationDto[];
  allHospitalizations: HospitalizationDto[];
  assignedSurveys: AssignedSurveyDto[];
}

export const HospitalizationFormSchema = z
  .object({
    note: z.string().refine(
      value => {
        // Remove HTML tags and check if empty
        const plainText = value.replace(/<[^>]*>/g, '');
        const trimmed = plainText.trim();
        const isEmptyHtml =
          trimmed === '' || trimmed === null || trimmed === undefined;
        return !isEmptyHtml;
      },
      {
        message: 'Note is required',
      }
    ),
  })
  .strict();
export type HospitalizationForm = z.infer<typeof HospitalizationFormSchema>;
